import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { DialogFooter } from '@ui/overlays/dialog/dialog-footer';
import { Button } from '@ui/buttons/button';
import { useDialogContext } from '@ui/overlays/dialog/dialog-context';
import { Product } from '../product';
import { useState } from 'react';
import { Trans } from '@ui/i18n/trans';
import { FormattedCurrency } from '@ui/i18n/formatted-currency';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useSettings } from '@ui/settings/use-settings';
import { getBootstrapData } from '@ui/bootstrap-data/bootstrap-data-store';
import { apiClient } from '@common/http/query-client';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';
import { showHttpErrorToast } from '@common/http/show-http-error-toast';
import { useAuth } from '@common/auth/use-auth';

interface SelectPlanPeriodDialogProps {
  product: Product;
}

export function SelectPlanPeriodDialog({ product }: SelectPlanPeriodDialogProps) {
  const { close } = useDialogContext();
  const { billing } = useSettings();
  const { isLoggedIn } = useAuth();
  const [isStartingGooglePlayBilling, setIsStartingGooglePlayBilling] = useState(false);

  const data = getBootstrapData();
  const detectedCurrency = data?.detected_currency || 'USD';

  // Filter prices by detected currency
  const filteredPrices = product.prices.filter(p => p.currency.toUpperCase() === detectedCurrency.toUpperCase());

  // Fallback to all prices if none match detected currency
  const pricesToShow = filteredPrices.length > 0 ? filteredPrices : product.prices;

  // Sort prices by duration (descending)
  const sortedPrices = [...pricesToShow].sort((a, b) => {
    const aMonths = a.interval === 'year' ? a.interval_count * 12 : a.interval_count;
    const bMonths = b.interval === 'year' ? b.interval_count * 12 : b.interval_count;
    return bMonths - aMonths;
  });

  const [selectedPriceId, setSelectedPriceId] = useState<number>(sortedPrices[0]?.id);

  const checkoutPrefix = (billing as any)?.checkout_route === 'alternative'
    ? '/purchase-subscription'
    : '/checkout';
  const selectedPrice = sortedPrices.find(price => price.id === selectedPriceId);
  const checkoutUrl = `${checkoutPrefix}/${product.id}/${selectedPriceId}`;
  const isAndroidApp = typeof window !== 'undefined' && Boolean(
    (window as any).__SINTONIZY_ANDROID_APP__ ||
      navigator.userAgent.includes('SintonizyAndroid')
  );

  const startGooglePlayBilling = async () => {
    if (!selectedPrice || isStartingGooglePlayBilling) return;

    if (!isLoggedIn) {
      close();
      window.location.href = `/login?redirectFrom=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    if (!selectedPrice.google_play_product_id || !selectedPrice.google_play_base_plan_id) {
      toast.danger(message('Este plano ainda não está disponível no Google Play.'));
      return;
    }

    setIsStartingGooglePlayBilling(true);
    try {
      const response = await apiClient
        .post('billing/google-play/intents', {
          product_id: product.id,
          price_id: selectedPrice.id,
        })
        .then(r => r.data);

      close();
      window.location.href = [
        'sintonizy-billing://checkout',
        `?intent=${encodeURIComponent(response.intent_token)}`,
        `&product=${encodeURIComponent(response.google_play_product_id)}`,
        `&basePlan=${encodeURIComponent(response.google_play_base_plan_id)}`,
      ].join('');
    } catch (error) {
      showHttpErrorToast(error);
    } finally {
      setIsStartingGooglePlayBilling(false);
    }
  };

  return (
    <Dialog size="sm" className="bg-paper text-main rounded-panel overflow-hidden border-none max-w-[500px]">
      <DialogHeader className="border-none mt-10">
        <div className="flex items-center gap-6 opacity-90">
          <div className="w-20 h-20 rounded-full border border-fg-base flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-fg-base" />
          </div>
          <span className="text-sm font-bold uppercase tracking-widest"><Trans message="Premium" /></span>
        </div>
        <h2 className="text-5xl font-black mt-16 tracking-tight">{product.name}</h2>
      </DialogHeader>
      <DialogBody className="mt-20">
        <p className="text-muted text-sm font-medium mb-32 leading-relaxed">
          <Trans message="Pague uma vez adiantado, recarregue quando quiser. Sem renovação automática." />
        </p>

        <div className="flex flex-col gap-12">
          {sortedPrices.map((price) => {
            const isSelected = selectedPriceId === price.id;
            const months = price.interval === 'year' ? price.interval_count * 12 : price.interval_count;
            const pricePerMonth = price.amount / months;

            const monthlyPrice = product.prices.find(p => p.interval === 'month' && p.interval_count === 1);
            const basePrice = monthlyPrice ? monthlyPrice.amount : (price.amount / months);
            const economy = (basePrice * months) - price.amount;

            const intervalLabel = price.interval === 'year'
              ? (price.interval_count > 1 ? <Trans message="years" /> : <Trans message="year" />)
              : (price.interval_count > 1 ? <Trans message="months" /> : <Trans message="month" />);

            return (
              <div
                key={price.id}
                onClick={() => setSelectedPriceId(price.id)}
                className={clsx(
                  "group relative flex items-center justify-between p-24 rounded-panel cursor-pointer transition-all duration-200 border-2",
                  isSelected ? "border-primary bg-background/40" : "border-divider hover:border-fg-base/10 bg-background/20"
                )}
              >
                <div className="flex-1">
                  <div className="text-xl font-bold flex items-center flex-wrap gap-x-6">
                    <FormattedCurrency value={price.amount} currency={price.currency} />
                    <span className="text-muted font-medium text-base">
                      <Trans message="por :count :interval" values={{ count: price.interval_count, interval: intervalLabel }} />
                    </span>
                  </div>
                  <div className="text-sm text-muted mt-4 font-medium italic">
                    <Trans message="Equivalente a :amount por mês" values={{
                      amount: <FormattedCurrency value={pricePerMonth} currency={price.currency} />
                    }} />
                  </div>
                  {economy > 1 && (
                    <div className="mt-12 bg-danger/10 text-danger text-[11px] font-black px-10 py-5 rounded-[4px] w-max uppercase tracking-wider">
                      <Trans message="Economize :amount" values={{
                        amount: <FormattedCurrency value={economy} currency={price.currency} />
                      }} />
                    </div>
                  )}
                </div>
                <div className={clsx(
                  "w-26 h-26 rounded-full border-2 flex items-center justify-center transition-all duration-200",
                  isSelected ? "border-primary bg-primary" : "border-divider group-hover:border-muted"
                )}>
                  {isSelected && <div className="w-10 h-10 rounded-full bg-on-primary" />}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-muted mt-32 leading-relaxed opacity-70 italic font-medium">
          <Trans message="Economize em comparação ao valor total de uma assinatura mensal com mesma duração. O preço equivalente mostra o valor dividido pelo número de meses relevantes apenas como uma referência. Preços do plano pré-pago são aplicáveis." />
        </p>
      </DialogBody>
      <DialogFooter className="mt-40 border-none pb-20">
        {isAndroidApp ? (
          <Button
            variant="flat"
            className="w-full rounded-full py-20 text-lg font-black bg-primary hover:bg-primary-dark hover:scale-[1.02] active:scale-[0.98] text-on-primary transition-all duration-200"
            disabled={isStartingGooglePlayBilling}
            onClick={startGooglePlayBilling}
          >
            <Trans message="Continuar" />
          </Button>
        ) : (
          <Button
            variant="flat"
            className="w-full rounded-full py-20 text-lg font-black bg-primary hover:bg-primary-dark hover:scale-[1.02] active:scale-[0.98] text-on-primary transition-all duration-200"
            elementType={Link}
            to={checkoutUrl}
            onClick={() => close()}
          >
            <Trans message="Continuar" />
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
